import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Store, Phone, Star, Tag, Plus, X } from 'lucide-react';

export default function BasicInfoTab({ formData, restaurant, onInputChange, onTagAdd, onTagRemove }) {
  const { t } = useTranslation();
  const [newTag, setNewTag] = useState('');

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter' && newTag.trim()) {
      onTagAdd(newTag.trim());
      setNewTag('');
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-blue-100 rounded-lg mr-4">
            <Store className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{t('settings.tabs.basic')}</h3>
            <p className="text-sm text-gray-600">{t('settings.basicInfo.description')}</p>
          </div>
        </div>
      </div>

      {/* Restaurant Names */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">{t('settings.restaurantName')}</h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.nameInBothLanguages')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.nameEn')}
              </label>
              <input
                type="text"
                value={formData.nameEn}
                onChange={(e) => onInputChange('nameEn', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-colors"
                placeholder={t('settings.nameEn')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.nameAr')}
              </label>
              <input
                type="text"
                value={formData.nameAr}
                onChange={(e) => onInputChange('nameAr', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-colors"
                placeholder={t('settings.nameAr')}
                dir="rtl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Descriptions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">{t('settings.description')}</h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.describeYourRestaurant')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.descriptionEn')}
              </label>
              <textarea
                value={formData.descriptionEn}
                onChange={(e) => onInputChange('descriptionEn', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t('settings.descriptionEn')}
              />
              <div className="text-xs text-gray-500 mt-1">
                {formData.descriptionEn?.length || 0} {t('settings.characters')}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.descriptionAr')}
              </label>
              <textarea
                value={formData.descriptionAr}
                onChange={(e) => onInputChange('descriptionAr', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t('settings.descriptionAr')}
                dir="rtl"
              />
              <div className="text-xs text-gray-500 mt-1">
                {formData.descriptionAr?.length || 0} {t('settings.characters')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">{t('settings.contactInformation')}</h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.phoneAndRating')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                {t('settings.phone')}
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => onInputChange('phone', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="+962 7X XXX XXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Star className="w-4 h-4 inline mr-1" />
                {t('settings.rating')}
              </label>
              <div className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.floor(formData.rating || 5) 
                          ? 'text-yellow-400 fill-current' 
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {formData.rating || 5.0}
                </span>
                <span className="text-sm text-gray-500">({t('settings.ratingReadOnly')})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">{t('settings.tags')}</h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.tagsDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex gap-3">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleTagKeyPress}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t('settings.addTag')}
              />
              <button
                onClick={() => {
                  if (newTag.trim()) {
                    onTagAdd(newTag.trim());
                    setNewTag('');
                  }
                }}
                className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('common.add')}
              </button>
            </div>
            
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    <Tag className="w-3 h-3 mr-2" />
                    {tag}
                    <button
                      onClick={() => onTagRemove(tag)}
                      className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {(!formData.tags || formData.tags.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('settings.noTagsYet')}</p>
                <p className="text-sm">{t('settings.addTagsToHelp')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
